import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Wrench, Phone, Mail, User, FileText, Search, Clock,
    Loader2, CheckCircle, AlertCircle, ArrowRight
} from 'lucide-react';
import { dataQualityService } from '../../services/dataQualityService';
import CustomSelect from '../shared/CustomSelect';
import { ErrorAlert } from '../shared/ErrorAlert';

const CleanupTools = () => {
    const navigate = useNavigate();

    // Individual tool state
    const [clientId, setClientId] = useState('');
    const [countryCode, setCountryCode] = useState('+254');
    const [fieldName, setFieldName] = useState('');
    const [newValue, setNewValue] = useState('');
    const [correctionReason, setCorrectionReason] = useState('');
    const [toolLoading, setToolLoading] = useState(null);
    const [toolResult, setToolResult] = useState(null);
    const [toolError, setToolError] = useState('');

    // Bulk operations state
    const [bulkAction, setBulkAction] = useState('');
    const [bulkClientType, setBulkClientType] = useState('');
    const [bulkClientIds, setBulkClientIds] = useState('');
    const [bulkCountryCode, setBulkCountryCode] = useState('+254');
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const [bulkError, setBulkError] = useState('');

    const clearToolState = () => {
        setToolResult(null);
        setToolError('');
    };

    const handleStandardizePhone = async () => {
        if (!clientId) return;
        try {
            clearToolState();
            setToolLoading('phone');
            const response = await dataQualityService.cleanup.standardizePhone(Number(clientId), countryCode);
            if (response.success) {
                setToolResult({ type: 'phone', data: response.data });
            } else {
                setToolError(response.message || 'Failed to standardize phone');
            }
        } catch (err) {
            setToolError(err.message || 'Failed to standardize phone');
        } finally {
            setToolLoading(null);
        }
    };

    const handleStandardizeEmail = async () => {
        if (!clientId) return;
        try {
            clearToolState();
            setToolLoading('email');
            const response = await dataQualityService.cleanup.standardizeEmail(Number(clientId));
            if (response.success) {
                setToolResult({ type: 'email', data: response.data });
            } else {
                setToolError(response.message || 'Failed to standardize email');
            }
        } catch (err) {
            setToolError(err.message || 'Failed to standardize email');
        } finally {
            setToolLoading(null);
        }
    };

    const handleStandardizeName = async () => {
        if (!clientId) return;
        try {
            clearToolState();
            setToolLoading('name');
            const response = await dataQualityService.cleanup.standardizeName(Number(clientId));
            if (response.success) {
                setToolResult({ type: 'name', data: response.data });
            } else {
                setToolError(response.message || 'Failed to standardize name');
            }
        } catch (err) {
            setToolError(err.message || 'Failed to standardize name');
        } finally {
            setToolLoading(null);
        }
    };

    const handleCorrectField = async () => {
        if (!clientId || !fieldName || newValue === '') return;
        try {
            clearToolState();
            setToolLoading('field');
            const response = await dataQualityService.cleanup.correctField(Number(clientId), {
                fieldName,
                newValue,
                reason: correctionReason,
            });
            if (response.success) {
                setToolResult({ type: 'field', data: response.data });
            } else {
                setToolError(response.message || 'Failed to correct field');
            }
        } catch (err) {
            setToolError(err.message || 'Failed to correct field');
        } finally {
            setToolLoading(null);
        }
    };

    const handleBulkCleanup = async () => {
        if (!bulkAction) return;
        try {
            setBulkResult(null);
            setBulkError('');
            setBulkLoading(true);

            const parsedIds = bulkClientIds.trim()
                ? bulkClientIds.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id))
                : [];

            const response = await dataQualityService.cleanup.bulk({
                action: bulkAction,
                clientIds: parsedIds.length > 0 ? parsedIds : null,
                clientType: bulkClientType || null,
                countryCode: bulkAction === 'STANDARDIZE_PHONE' ? bulkCountryCode : null,
            });

            if (response.success && response.data) {
                setBulkResult(response.data);
            } else {
                setBulkError(response.message || 'Bulk cleanup failed');
            }
        } catch (err) {
            setBulkError(err.message || 'Bulk cleanup failed');
        } finally {
            setBulkLoading(false);
        }
    };

    const BULK_ACTION_OPTIONS = [
        { value: '', label: 'Select Action' },
        { value: 'STANDARDIZE_PHONE', label: 'Standardize Phone Numbers' },
        { value: 'STANDARDIZE_EMAIL', label: 'Standardize Emails' },
        { value: 'STANDARDIZE_NAME', label: 'Standardize Names' },
    ];

    const CLIENT_TYPE_OPTIONS = [
        { value: '', label: 'All Client Types' },
        { value: 'RETAIL', label: 'Retail' },
        { value: 'CORPORATE', label: 'Corporate' },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h2 className="text-2xl font-semibold text-gray-900">Cleanup Tools</h2>
                <p className="text-gray-600 mt-1">Standardize and fix client data</p>
            </div>

            {/* Individual Tools */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Wrench className="w-5 h-5" />
                    Individual Client Tools
                </h3>

                {/* Client ID Input */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Client ID <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="number"
                        value={clientId}
                        onChange={(e) => { setClientId(e.target.value); clearToolState(); }}
                        className="w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter client ID"
                    />
                </div>

                {/* Tool Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Standardize Phone */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <Phone className="w-5 h-5 text-blue-600" />
                            <p className="font-medium text-gray-900">Standardize Phone</p>
                        </div>
                        <div className="flex items-center gap-2 mb-3">
                            <input
                                type="text"
                                value={countryCode}
                                onChange={(e) => setCountryCode(e.target.value)}
                                className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="+254"
                            />
                            <button
                                onClick={handleStandardizePhone}
                                disabled={!clientId || toolLoading === 'phone'}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 text-sm flex items-center gap-1"
                            >
                                {toolLoading === 'phone' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Standardize'}
                            </button>
                        </div>
                    </div>

                    {/* Standardize Email */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <Mail className="w-5 h-5 text-green-600" />
                            <p className="font-medium text-gray-900">Standardize Email</p>
                        </div>
                        <button
                            onClick={handleStandardizeEmail}
                            disabled={!clientId || toolLoading === 'email'}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 text-sm flex items-center gap-1"
                        >
                            {toolLoading === 'email' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Standardize'}
                        </button>
                    </div>

                    {/* Standardize Name */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <User className="w-5 h-5 text-purple-600" />
                            <p className="font-medium text-gray-900">Standardize Name</p>
                        </div>
                        <button
                            onClick={handleStandardizeName}
                            disabled={!clientId || toolLoading === 'name'}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 text-sm flex items-center gap-1"
                        >
                            {toolLoading === 'name' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Standardize'}
                        </button>
                    </div>

                    {/* Correct Field */}
                    <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <FileText className="w-5 h-5 text-orange-600" />
                            <p className="font-medium text-gray-900">Correct Field</p>
                        </div>
                        <div className="space-y-2">
                            <input
                                type="text"
                                value={fieldName}
                                onChange={(e) => setFieldName(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Field name (e.g., phoneNumber)"
                            />
                            <input
                                type="text"
                                value={newValue}
                                onChange={(e) => setNewValue(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="New value"
                            />
                            <input
                                type="text"
                                value={correctionReason}
                                onChange={(e) => setCorrectionReason(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Reason (optional)"
                            />
                            <button
                                onClick={handleCorrectField}
                                disabled={!clientId || !fieldName || newValue === '' || toolLoading === 'field'}
                                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition disabled:opacity-50 text-sm flex items-center gap-1"
                            >
                                {toolLoading === 'field' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Correct'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tool Result Display */}
                {toolError && (
                    <div className="mt-4">
                        <ErrorAlert error={toolError} />
                    </div>
                )}
                {toolResult && (
                    <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <p className="font-medium text-green-800">
                                {toolResult.type === 'phone' && 'Phone standardized successfully'}
                                {toolResult.type === 'email' && 'Email standardized successfully'}
                                {toolResult.type === 'name' && 'Name standardized successfully'}
                                {toolResult.type === 'field' && 'Field corrected successfully'}
                            </p>
                        </div>
                        {toolResult.data && (
                            <div className="text-sm text-green-700">
                                <p>Client: {toolResult.data.firstName} {toolResult.data.lastName || toolResult.data.companyName}</p>
                                {toolResult.data.phoneNumber && <p>Phone: {toolResult.data.phoneNumber}</p>}
                                {toolResult.data.email && <p>Email: {toolResult.data.email}</p>}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Bulk Operations */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Wrench className="w-5 h-5" />
                    Bulk Operations
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Action <span className="text-red-500">*</span></label>
                        <CustomSelect
                            value={bulkAction}
                            onChange={setBulkAction}
                            options={BULK_ACTION_OPTIONS}
                            placeholder="Select Action"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Client Type</label>
                        <CustomSelect
                            value={bulkClientType}
                            onChange={setBulkClientType}
                            options={CLIENT_TYPE_OPTIONS}
                            placeholder="All Types"
                        />
                    </div>
                    {bulkAction === 'STANDARDIZE_PHONE' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Country Code</label>
                            <input
                                type="text"
                                value={bulkCountryCode}
                                onChange={(e) => setBulkCountryCode(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="+254"
                            />
                        </div>
                    )}
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client IDs (optional, comma-separated)
                    </label>
                    <input
                        type="text"
                        value={bulkClientIds}
                        onChange={(e) => setBulkClientIds(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 1, 5, 12, 34 (leave empty for all clients)"
                    />
                </div>

                <button
                    onClick={handleBulkCleanup}
                    disabled={!bulkAction || bulkLoading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                    {bulkLoading ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Running...
                        </>
                    ) : (
                        'Run Bulk Cleanup'
                    )}
                </button>

                {bulkError && (
                    <div className="mt-4">
                        <ErrorAlert error={bulkError} />
                    </div>
                )}
                {bulkResult && (
                    <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <p className="font-medium text-green-800">Bulk cleanup completed</p>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                            <div className="bg-white rounded p-3 border border-green-200">
                                <p className="text-sm text-gray-500">Processed</p>
                                <p className="text-xl font-bold text-gray-900">{bulkResult.totalProcessed}</p>
                            </div>
                            <div className="bg-white rounded p-3 border border-green-200">
                                <p className="text-sm text-gray-500">Corrected</p>
                                <p className="text-xl font-bold text-green-600">{bulkResult.totalCorrected}</p>
                            </div>
                            <div className="bg-white rounded p-3 border border-green-200">
                                <p className="text-sm text-gray-500">Skipped</p>
                                <p className="text-xl font-bold text-gray-500">{bulkResult.totalSkipped}</p>
                            </div>
                            <div className="bg-white rounded p-3 border border-green-200">
                                <p className="text-sm text-gray-500">Errors</p>
                                <p className="text-xl font-bold text-red-600">{bulkResult.totalErrors}</p>
                            </div>
                        </div>
                        {bulkResult.errorDetails?.length > 0 && (
                            <div className="mt-3">
                                <p className="text-sm font-medium text-red-700 mb-1">Error Details:</p>
                                <ul className="list-disc list-inside text-sm text-red-600 space-y-1">
                                    {bulkResult.errorDetails.map((err, idx) => (
                                        <li key={idx}>{err}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Links</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <button
                        onClick={() => navigate('/data-quality/cleanup/missing-field')}
                        className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition text-left flex items-center justify-between"
                    >
                        <div className="flex items-center gap-3">
                            <Search className="w-5 h-5 text-blue-600" />
                            <div>
                                <p className="font-medium text-gray-900">Find Missing Fields</p>
                                <p className="text-sm text-gray-500">Search clients missing a specific field</p>
                            </div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-400" />
                    </button>
                    <button
                        onClick={() => navigate('/data-quality/cleanup/stale')}
                        className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition text-left flex items-center justify-between"
                    >
                        <div className="flex items-center gap-3">
                            <Clock className="w-5 h-5 text-orange-600" />
                            <div>
                                <p className="font-medium text-gray-900">Stale Records</p>
                                <p className="text-sm text-gray-500">Find clients with no recent updates</p>
                            </div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-400" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CleanupTools;
